import React, { useState, useEffect } from 'react';
import { PasskeyService, Passkey } from '../../services/auth/passkeyService';
import { KeyRound, Plus, Trash2, Edit2, Check, X, Smartphone, Usb, AlertCircle, Info, Loader2, CheckCircle } from 'lucide-react';
import { TWHoldButton } from '../ui/TWHoldButton';
import toast from '../../utils/toast';
import { formatDateTimeString } from '../../utils/dateUtils';
import clsx from 'clsx';

interface PasskeyManagementProps {
  onRenderHeaderAction?: (button: React.ReactNode) => void;
}

export const PasskeyManagement: React.FC<PasskeyManagementProps> = ({ onRenderHeaderAction }) => {
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [capabilities, setCapabilities] = useState<any>(null);

  useEffect(() => {
    loadPasskeys();
    checkCapabilities();
  }, []);

  const loadPasskeys = async () => {
    try {
      setIsLoading(true);
      const data = await PasskeyService.listPasskeys();
      setPasskeys(data);
    } catch (error) {
      toast.error('Fehler beim Laden der Passkeys');
    } finally {
      setIsLoading(false);
    }
  };

  const checkCapabilities = async () => {
    const caps = await PasskeyService.getCapabilities();
    setCapabilities(caps);
  };

  const handleAdd = async () => {
    setIsAdding(true);
    try {
      const deviceName = prompt('Gerätename (optional, z.B. "iPhone 15 Pro"):');
      await PasskeyService.registerPasskey(deviceName || undefined);
      await loadPasskeys();
      toast.success('Passkey erfolgreich hinzugefügt!');
    } catch (error: any) {
      toast.error(error.message || 'Fehler beim Hinzufügen');
    } finally {
      setIsAdding(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await PasskeyService.deletePasskey(id);
      await loadPasskeys();
      toast.success('Passkey gelöscht');
    } catch (error: any) {
      toast.error(error.message || 'Fehler beim Löschen');
    }
  };

  const handleRename = async (id: string) => {
    try {
      await PasskeyService.renamePasskey(id, editName);
      await loadPasskeys();
      setEditingId(null);
      toast.success('Name geändert');
    } catch (error: any) {
      toast.error(error.message || 'Fehler beim Umbenennen');
    }
  };

  const getDeviceIcon = (deviceType: string, transports: string[]) => {
    if (transports.includes('internal')) {
      return <Smartphone className="w-5 h-5 text-orange-500" />;
    }
    if (transports.includes('usb')) {
      return <Usb className="w-5 h-5 text-status-success" />;
    }
    return <KeyRound className="w-5 h-5 text-orange-500" />;
  };

  const formatDate = (date: string | null) => {
    if (!date) return 'Nie verwendet';
    return formatDateTimeString(date);
  };

  // Button für Header-Action (muss vor useEffect definiert werden)
  const addButton = capabilities?.supported && capabilities?.platformAuthenticator ? (
    <button
      onClick={handleAdd}
      disabled={isAdding}
      className={clsx(
        'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg font-medium text-sm transition-all duration-200',
        'bg-orange-500 text-white shadow-sm',
        'hover:bg-orange-600 hover:shadow-md',
        'focus:outline-none focus:ring-2 focus:ring-orange-500/30',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'select-none touch-manipulation'
      )}
    >
      {isAdding ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Plus className="w-4 h-4" />
      )}
      <span className="hidden sm:inline">{isAdding ? 'Warte...' : 'Hinzufügen'}</span>
    </button>
  ) : null;

  // Button an Parent übergeben für Header-Platzierung (MUSS vor jedem Return stehen!)
  useEffect(() => {
    if (onRenderHeaderAction) {
      onRenderHeaderAction(addButton);
    }
  }, [onRenderHeaderAction, isAdding, capabilities?.supported, capabilities?.platformAuthenticator]);

  // Nicht unterstützt
  if (!capabilities?.supported) {
    return (
      <div className="p-4 rounded-lg flex items-start gap-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/30">
        <AlertCircle className="w-5 h-5 shrink-0 mt-0.5 text-status-warning" />
        <div>
          <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
            Nicht unterstützt
          </p>
          <p className="text-xs mt-0.5 text-gray-500 dark:text-gray-400">
            Passkeys werden von diesem Browser nicht unterstützt.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* Passkey-Liste */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : passkeys.length === 0 ? (
        <div className="text-center py-8 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
          <KeyRound className="mx-auto mb-3 w-10 h-10 text-gray-400 dark:text-gray-500" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Noch keine Passkeys registriert
          </p>
          <p className="text-xs mt-1 text-gray-400 dark:text-gray-500">
            Füge einen Passkey hinzu für schnelleren und sichereren Login
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {passkeys.map(passkey => (
            <div
              key={passkey.id}
              className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {/* Device Icon */}
                  <div className="p-2.5 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shrink-0">
                    {getDeviceIcon(passkey.device_type, passkey.transports)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {editingId === passkey.id ? (
                      // Edit Mode
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          autoFocus
                          className={clsx(
                            'flex-1 px-3 py-1.5 rounded-lg text-sm',
                            'bg-white dark:bg-gray-900',
                            'text-gray-900 dark:text-gray-100',
                            'border border-gray-200 dark:border-gray-700',
                            'focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500'
                          )}
                        />
                        <button
                          onClick={() => handleRename(passkey.id)}
                          className="p-1.5 rounded-lg text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                          title="Speichern"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="p-1.5 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                          title="Abbrechen"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      // View Mode
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100 truncate">
                          {passkey.device_name}
                        </h4>
                        <button
                          onClick={() => {
                            setEditingId(passkey.id);
                            setEditName(passkey.device_name);
                          }}
                          className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shrink-0"
                          title="Umbenennen"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="mt-1.5 space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                      <p>Erstellt: {formatDate(passkey.created_at)}</p>
                      <p>Zuletzt verwendet: {formatDate(passkey.last_used_at)}</p>
                    </div>
                  </div>
                </div>

                {/* Delete Button */}
                <div className="shrink-0">
                  <TWHoldButton
                    onConfirm={() => handleDelete(passkey.id)}
                    holdDuration={3000}
                    icon={<Trash2 />}
                    text="3s halten"
                    size={32}
                    variant="ghost"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info-Box */}
      <div className="p-4 rounded-lg flex items-start gap-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900/30">
        <Info className="w-5 h-5 shrink-0 mt-0.5 text-status-info" />
        <div>
          <p className="text-sm font-medium text-blue-700 dark:text-blue-400">
            Was sind Passkeys?
          </p>
          <ul className="mt-2 space-y-1.5 text-xs text-gray-500 dark:text-gray-400">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-3.5 h-3.5 text-status-success shrink-0" />
              <span>Sicherer als Passwörter (Phishing-resistent)</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-3.5 h-3.5 text-status-success shrink-0" />
              <span>Schneller Login mit {capabilities?.authenticatorType || 'Face ID / Touch ID'}</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-3.5 h-3.5 text-status-success shrink-0" />
              <span>Synchronisiert zwischen deinen Geräten</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
